import React from "react";
import { format, isSameDay } from "date-fns";
import Loader from "../../../components/Loader/Loader";
import StatusChip from "../../../components/StatusChip/StatusChip";
import { useStudentWordOfTheDay } from "./hooks/useStudentWordOfTheDay";
import { useStudentSession } from "../context/StudentSessionContext";
import { sanitizeHtml } from "../../../utils/sanitizeHtml";
import WordOfTheDayCalendar from "./components/WordOfTheDayCalendar";
import "./StudentWordOfTheDay.css";

const pick = (word, keys) => {
  for (const key of keys) {
    if (word?.[key]) return word[key];
  }
  return "";
};

const WordOfTheDayEntry = ({ word, index }) => {
  const text = pick(word, ["word", "title", "name"]);
  const pronunciation = pick(word, ["pronunciation", "phonetic"]);
  const partOfSpeech = pick(word, ["part_of_speech", "partOfSpeech", "type"]);
  const meaning = pick(word, ["meaning", "definition", "description"]);
  const example = pick(word, ["example", "usage", "example_sentence"]);

  return (
    <div className="sp-card sp-wotd-card" key={word?.id ?? index}>
      <div className="sp-wotd-head">
        <h3 className="sp-wotd-word">{text || "Untitled"}</h3>
        {(partOfSpeech || pronunciation) && (
          <div className="sp-wotd-meta">
            {partOfSpeech && <span className="sp-wotd-pos">{partOfSpeech}</span>}
            {pronunciation && <span>{pronunciation}</span>}
          </div>
        )}
      </div>

      {/* Confirmed against the real backend (2026-10-22): rich-text fields
          on this backend (FAQ description, circular_desc, event
          description) all come back as HTML ("<p>...</p>"), not plain
          text — meaning/example are rendered as HTML for the same
          reason, instead of showing the raw tags on screen. */}
      {meaning && (
        <div className="sp-wotd-row">
          <span className="sp-wotd-label">Meaning</span>
          {/* eslint-disable-next-line react/no-danger -- sanitized via DOMPurify in sanitizeHtml() above */}
          <div className="sp-wotd-body" dangerouslySetInnerHTML={{ __html: sanitizeHtml(meaning) }} />
        </div>
      )}

      {example && (
        <div className="sp-wotd-row">
          <span className="sp-wotd-label">Example</span>
          {/* eslint-disable-next-line react/no-danger -- sanitized via DOMPurify in sanitizeHtml() above */}
          <div className="sp-wotd-body sp-wotd-example" dangerouslySetInnerHTML={{ __html: sanitizeHtml(example) }} />
        </div>
      )}
    </div>
  );
};

const StudentWordOfTheDay = () => {
  const { student } = useStudentSession();
  const { words, isLoading, selectedDate, selectDate, month, setMonth } = useStudentWordOfTheDay(student?.id);

  return (
    <div className="sp-wotd-page">
      <h2 className="sp-section-title">Word of the Day</h2>

      <div className="wotd-layout">
        <div className="wotd-layout-main">
          <div className="wotd-date-row">
            <span className="wotd-selected-date">{format(selectedDate, "EEEE, d MMMM yyyy")}</span>
            {isSameDay(selectedDate, new Date()) && <StatusChip tone="primary">Today</StatusChip>}
          </div>

          {isLoading ? (
            <Loader />
          ) : words.length === 0 ? (
            <div className="sp-card sp-wotd-empty">
              <div className="sp-empty-state">
                <p className="mb-0">Nothing published for this date.</p>
              </div>
            </div>
          ) : (
            <div className="sp-wotd-list">
              {words.map((word, index) => (
                <WordOfTheDayEntry word={word} index={index} key={word?.id ?? index} />
              ))}
            </div>
          )}
        </div>

        <div className="wotd-layout-calendar">
          <WordOfTheDayCalendar
            month={month}
            selectedDate={selectedDate}
            onMonthChange={setMonth}
            onSelectDate={selectDate}
          />
        </div>
      </div>
    </div>
  );
};

export default StudentWordOfTheDay;
